"use strict";
var _a;
Object.defineProperty(exports, "__esModule", { value: true });
exports.applyDateFilter = exports.EntityService = void 0;
const tslib_1 = require("tslib");
const common_1 = require("@nestjs/common");
const typeorm_1 = require("typeorm");
const response_utils_1 = require("./response.utils");
const constants_1 = require("../constants");
const user_1 = require("../../../../../apps/services/service-lib/src/lib/entities/user");
const look_up_entity_1 = require("../../../../../apps/services/service-lib/src/lib/entities/look-up.entity");
const helper_utils_1 = require("./helper.utils");
const class_validator_1 = require("class-validator");
let EntityService = class EntityService {
    constructor(dataSource) {
        this.dataSource = dataSource;
    }
    /**
     * Retrieves data for the specified entity.
     */
    async getData(entity, page, limit, search, sortBy, sort = [], relations, where, select, searchArray, userFilter) {
        try {
            const repository = this.dataSource.getRepository(entity);
            const skip = (page - 1) * limit;
            // Build search and order conditions using helper functions
            let whereCondition = (0, helper_utils_1.buildWhereCondition)(repository, where, searchArray, relations);
            if (userFilter) {
                whereCondition = applyRbacFilters(whereCondition, userFilter);
            }
            const orderCondition = (0, helper_utils_1.buildOrderCondition)(repository, sort, relations);
            // Fetch data
            const [data, total] = await repository.findAndCount({
                skip,
                take: limit,
                where: whereCondition,
                relations,
                order: orderCondition,
                select: select ? select : undefined,
            });
            // Recursively remove unwanted fields
            const filteredData = data.map((item) => removeUnwantedFields(item));
            return { data: filteredData, count: total };
        }
        catch (error) {
            console.error(error);
            (0, response_utils_1.createErrorResponse)(common_1.HttpStatus.BAD_REQUEST, error.message);
            throw new Error("Failed to fetch entity data.");
        }
    }
    /**
     * Retrieves data for the specified entity by ID.
     */
    async getDataById(entity, id, relations) {
        try {
            const repository = this.dataSource.getRepository(entity);
            const data = await repository.findOne({
                where: { id },
                relations,
            });
            if (!data) {
                (0, response_utils_1.createErrorResponse)(common_1.HttpStatus.NOT_FOUND, `Entity with ID ${id} not found`);
            }
            return removeUnwantedFields(data);
        }
        catch (error) {
            console.error("Error fetching entity data by ID:", error);
            (0, response_utils_1.createErrorResponse)(common_1.HttpStatus.INTERNAL_SERVER_ERROR, error.message);
            throw new Error("Failed to fetch entity data by ID.");
        }
    }
    async fetchEntityList(entity, page, limit, sort = [], relations, where, select, searchArray, userFilter, searchString, searchOn, dateFilter, period, preserveCreatedAt, entityIds) {
        try {
            const repo = this.dataSource.getRepository(entity);
            const qb = repo.createQueryBuilder("main");
            // 1) Apply user filter if provided
            // 1) top-level WHERE
            if (where) {
                qb.where(where);
            }
            if (userFilter) {
                applyCommonfilters(qb, userFilter);
            }
            // 2) normalize relations into dot-paths
            const relationPaths = typeof relations === "string"
                ? [relations]
                : Array.isArray(relations)
                    ? relations
                    : [];
            // 3) JOIN & SELECT
            for (const path of relationPaths) {
                const alias = path.replace(/\./g, "_");
                const segments = path.split(".");
                const parent = segments.length > 1 ? segments.slice(0, -1).join("_") : "main";
                const relationName = segments[segments.length - 1];
                qb.leftJoinAndSelect(`${parent === "main" ? "main" : parent}.${relationName}`, alias);
            }
            // 4) searchArray → IN or ILIKE
            if (Array.isArray(searchArray) && searchArray.length > 0) {
                searchArray.forEach(({ searchBy, searchValue }, idx) => {
                    const param = `param_${idx}`;
                    let alias, column;
                    if (searchBy.includes(".")) {
                        const parts = searchBy.split(".");
                        column = parts.pop();
                        alias = parts.join("_");
                    }
                    else {
                        alias = "main";
                        column = searchBy;
                    }
                    if (Array.isArray(searchValue)) {
                        qb.andWhere(`${alias}.${column} IN (:...${param})`, {
                            [param]: searchValue,
                        });
                    }
                    else {
                        qb.andWhere(`${alias}.${column} ILIKE :${param}`, {
                            [param]: `%${searchValue}%`,
                        });
                    }
                });
            }
            // 5) search
            if (searchString) {
                (0, helper_utils_1.applySearchConditions)(qb, searchString, searchOn || []);
            }
            // 6) date filter
            if (dateFilter) {
                (0, exports.applyDateFilter)(qb, dateFilter.field, dateFilter.from, dateFilter.to);
            }
            if (period) {
                (0, exports.applyDateFilter)(qb, period.field, period.from, period.to);
            }
            // 7) sorting
            for (const { field, order } of sort) {
                let alias, column;
                if (field.includes(".")) {
                    const parts = field.split(".");
                    column = parts.pop();
                    alias = parts.join("_");
                }
                else {
                    alias = "main";
                    column = field;
                }
                qb.addOrderBy(`${alias}.${column}`, order);
            }
            // 8) pagination & select
            qb.skip((page - 1) * limit).take(limit);
            // Handle select fields
            if (select?.length) {
                qb.select(select.map((f) => {
                    if ((0, class_validator_1.isString)(f) && f.includes(".")) {
                        const parts = f.split(".");
                        const column = parts.pop();
                        const alias = parts.join("_"); // handles nested relations
                        return `${alias}.${column}`;
                    }
                    return `main.${String(f)}`;
                }));
            }
            // 9) inspect SQL and run
            const [rows, total] = await qb.getManyAndCount();
            let filteredData = rows.map((row) => {
                const filteredRow = removeUnwantedFields(row);
                // Conditionally preserve `createdAt`
                if (preserveCreatedAt && row.createdAt) {
                    filteredRow.createdAt = row.createdAt;
                }
                return filteredRow;
            });
            if (entityIds && entityIds.length > 0) {
                const existingIds = new Set(rows.map((r) => r.id));
                const missingIds = entityIds.filter((id) => !existingIds.has(id));
                if (missingIds.length > 0) {
                    const additional = await repo.find({
                        where: { id: (0, typeorm_1.In)(missingIds) },
                        relations: relationPaths,
                        select: select,
                    });
                    const processed = additional.map((row) => removeUnwantedFields(row));
                    filteredData = filteredData.concat(processed);
                }
            }
            return {
                data: filteredData,
                count: total,
            };
        }
        catch (error) {
            console.error("Error in fetchEntityList:", error);
            throw new Error("Failed to fetch entity list.");
        }
    }
    async getFiltersData(entity, page, limit, search, searchBy, sortBy, sort = "ASC", relations) {
        try {
            const repository = this.dataSource.getRepository(entity);
            const skip = (page - 1) * limit;
            // **Build Search Condition**
            let whereCondition = {};
            if (search && searchBy) {
                const searchColumn = repository.metadata.columns.find((col) => col.propertyName === searchBy);
                if (!searchColumn) {
                    console.error(`Invalid searchBy column: ${searchBy}`);
                    throw new Error(`Invalid searchBy column: ${searchBy}`);
                }
                // **Handle Different Data Types**
                const columnType = searchColumn.type;
                if (["varchar", "text"].includes(columnType)) {
                    whereCondition = {
                        [searchBy]: (0, typeorm_1.ILike)(`%${search}%`),
                    };
                }
                else if (["int", "bigint", "decimal", "float"].includes(columnType)) {
                    whereCondition = {
                        [searchBy]: Number(search),
                    };
                }
                else if (columnType &&
                    [
                        "timestamp",
                        "datetime",
                        "date",
                        "timestamptz",
                        "timestamp with time zone",
                    ].includes(columnType)) {
                    const startOfDay = new Date(search);
                    startOfDay.setUTCHours(0, 0, 0, 0);
                    const endOfDay = new Date(search);
                    endOfDay.setUTCHours(23, 59, 59, 999);
                    whereCondition = {
                        [searchBy]: (0, typeorm_1.Between)(startOfDay, endOfDay),
                    };
                }
                else {
                    console.error(`Unsupported column type for search: ${columnType}`);
                    throw new Error(`Unsupported column type for search: ${columnType}`);
                }
            }
            // **Sorting Condition**
            const validSortColumn = sortBy &&
                repository.metadata.columns.some((col) => col.propertyName === sortBy)
                ? sortBy
                : "id";
            const orderCondition = {
                [validSortColumn]: sort,
            };
            // **Fetch Data**
            const [data, total] = await repository.findAndCount({
                skip,
                take: limit,
                where: whereCondition,
                relations,
                order: orderCondition,
            });
            // **Recursively remove unwanted fields**
            const filteredData = data.map((item) => removeUnwantedFields(item));
            return { data: filteredData, count: total };
        }
        catch (error) {
            console.error("Error in getFiltersData:", error);
            return (0, response_utils_1.createErrorResponse)(common_1.HttpStatus.INTERNAL_SERVER_ERROR, error.message);
        }
    }
    async getListOfValues(entity, selectedFields, whereCondition, relations) {
        try {
            const repository = await this.dataSource.getRepository(entity);
            // Fetch data with selected fields and relations
            const data = await repository.findOne({
                select: selectedFields,
                relations: relations,
                where: whereCondition,
            });
            return data;
        }
        catch (error) {
            throw new Error("Failed to fetch list of values.");
        }
    }
    async getLookupValues(keys) {
        if (!keys || keys.length === 0) {
            throw new Error("Keys array is required.");
        }
        const repository = this.dataSource.getRepository(look_up_entity_1.LookUp);
        const lookUps = await repository.find({
            where: { id: (0, typeorm_1.In)(keys) },
            select: ["id", "lookUpValue"],
        });
        return lookUps.map((lookup) => ({
            id: lookup.id,
            lookUpValue: lookup.lookUpValue,
        }));
    }
    async getUserValues(keys) {
        if (!keys || keys.length === 0) {
            throw new Error("Keys array is required.");
        }
        const repository = this.dataSource.getRepository(user_1.User);
        const userDetails = await repository.find({
            where: { userId: (0, typeorm_1.In)(keys) },
            select: ["userId", "firstName", "lastName"],
        });
        return userDetails.map((userData) => ({
            userId: userData.userId,
            firstName: userData.firstName,
            lastName: userData.lastName,
        }));
    }
    /**
     * Retrieves all the data ids for the specified entity.
     */
    async getEntityMapByIds(entity, select, // Enforce type safety for selected field
    where) {
        try {
            const repository = this.dataSource.getRepository(entity);
            const data = await repository.find({
                where,
                select: [select],
            });
            if (!data || data.length === 0) {
                return [];
            }
            return data.map((item) => item[select]);
        }
        catch (error) {
            console.error("Error in getEntityMapByIds:", error);
            return [];
        }
    }
    /**
     * Removes mapped data by hard deleting (if MAP_REMOVED) or soft deleting (by setting deletedAt) the specified IDs for the given entity.
     */
    async deleteEntityMapByIds(entity, deleteIds, select, deletionType) {
        try {
            const repository = this.dataSource.getRepository(entity);
            if (deletionType === constants_1.MAPPED_DATA_DELETION.MAP_REMOVED) {
                await Promise.all(deleteIds.map((mappedId) => repository.delete({ [select]: mappedId })));
            }
            else {
                await Promise.all(deleteIds.map((mappedId) => repository.update({ [select]: mappedId }, {
                    deletedAt: new Date(),
                })));
            }
            return true;
        }
        catch (error) {
            return null;
        }
    }
    async deleteFileUploadByIds(fileUploadEntity, entity, select, where, deletionType) {
        try {
            const fileRepository = this.dataSource.getRepository(fileUploadEntity);
            const deleteIds = await this.getEntityMapByIds(entity, select, where);
            if (deleteIds.length > 0) {
                if (deletionType === constants_1.MAPPED_DATA_DELETION.MAP_REMOVED) {
                    await Promise.all(deleteIds.map((mappedId) => fileRepository.delete({ id: mappedId })));
                }
                else {
                    await Promise.all(deleteIds.map((mappedId) => fileRepository.update({ id: mappedId }, {
                        deletedAt: new Date(),
                    })));
                }
            }
            return true;
        }
        catch (error) {
            console.error("Error deleting file uploads:", error);
            return null;
        }
    }
};
exports.EntityService = EntityService;
exports.EntityService = EntityService = tslib_1.__decorate([
    (0, common_1.Injectable)(),
    tslib_1.__metadata("design:paramtypes", [typeof (_a = typeof typeorm_1.DataSource !== "undefined" && typeorm_1.DataSource) === "function" ? _a : Object])
], EntityService);
/**
 * Recursively removes `createdBy`, `updatedBy`, `createdAt`, and `updatedAt` from an object.
 */
function removeUnwantedFields(obj) {
    if (Array.isArray(obj)) {
        return obj.map(removeUnwantedFields);
    }
    else if (obj instanceof Date) {
        // Convert Date objects to ISO string
        return obj.toISOString();
    }
    else if (obj !== null && typeof obj === "object") {
        return Object.fromEntries(Object.entries(obj)
            .filter(([key]) => !["createdBy", "updatedBy", "createdAt", "updatedAt"].includes(key))
            .map(([key, value]) => [key, removeUnwantedFields(value)]));
    }
    return obj;
}
function applyCommonfilters(qb, userFilter) {
    if (userFilter) {
        const createdByIds = [];
        const taggedByIds = [];
        // Apply 'createdBy' filter if any of these conditions match
        if (userFilter.userId)
            createdByIds.push(userFilter.userId);
        if (userFilter.reporteeUserIds?.length)
            createdByIds.push(...userFilter.reporteeUserIds);
        if (userFilter.custom?.length)
            createdByIds.push(...userFilter.custom);
        if (userFilter.tagged?.length)
            taggedByIds.push(...userFilter.tagged);
        if (taggedByIds.length > 0 && createdByIds.length > 0) {
            qb.andWhere(new typeorm_1.Brackets((qb1) => {
                qb1.where("main.createdBy IN (:...createdByIds)", { createdByIds });
                qb1.orWhere("main.id IN (:...taggedByIds)", { taggedByIds });
            }));
        }
        else if (createdByIds.length > 0) {
            qb.andWhere("main.createdBy IN (:...createdByIds)", { createdByIds });
        }
        // Apply filters for org, branch, and lob (refactored)
        const filterKeys = ["org", "branch", "lob"];
        filterKeys.forEach((filterKey, index) => {
            const filter = userFilter[filterKey];
            if (filter && filter.key) {
                applyfilterOnKey(qb, filterKey, filter, index);
            }
            else {
                console.warn(`getData ▶ userFilter.${filterKey} is missing or invalid.`);
            }
        });
    }
}
// Helper function to apply the filters
function applyfilterOnKey(qb, filterKey, filter, index) {
    const param = `param_${index}`;
    let alias, column;
    if (filter.key.includes(".")) {
        const parts = filter.key.split(".");
        column = parts.pop();
        alias = parts.join("_");
    }
    else {
        alias = "main";
        column = filter.key;
    }
    // Check if the value is an array
    if (Array.isArray(filter?.value)) {
        qb.andWhere(`${alias}.${column} IN (:...${param})`, {
            [param]: filter?.value,
        });
    }
}
function objectDeepMerge(target, source) {
    for (const key of Object.keys(source)) {
        const sourceValue = source[key];
        const targetValue = target[key]; // type-safe access workaround
        if (typeof targetValue === "object" &&
            targetValue !== null &&
            typeof sourceValue === "object" &&
            sourceValue !== null) {
            objectDeepMerge(targetValue, sourceValue);
        }
        else {
            target[key] = sourceValue;
        }
    }
    return target;
}
// Supports both nested & direct keys
function buildNestedObject(path, value) {
    if (!path.includes(".")) {
        return { [path]: (0, typeorm_1.In)(value) };
    }
    return path
        .split(".")
        .reverse()
        .reduce((acc, key) => ({ [key]: acc }), (0, typeorm_1.In)(value));
}
function applyRbacFilters(baseWhere, userFilter) {
    const rbacFilter = {};
    const createdByList = [
        userFilter.userId,
        ...(userFilter.reporteeUserIds || []),
        ...(userFilter.custom || []),
        ...(userFilter.tagged || []),
    ];
    if (createdByList.length > 0) {
        rbacFilter.createdBy = (0, typeorm_1.In)(createdByList);
    }
    const nestedFilters = [userFilter.org, userFilter.branch, userFilter.lob];
    nestedFilters.forEach((filter) => {
        if (filter?.key && filter.value.length > 0) {
            const nestedObj = buildNestedObject(filter.key, filter.value);
            if (typeof nestedObj === "object" && nestedObj !== null) {
                objectDeepMerge(rbacFilter, nestedObj);
            }
        }
    });
    if (Array.isArray(baseWhere)) {
        return baseWhere.map((cond) => ({ ...cond, ...rbacFilter }));
    }
    else if (baseWhere && Object.keys(baseWhere).length > 0) {
        return { ...baseWhere, ...rbacFilter };
    }
    else {
        return rbacFilter;
    }
}
/**
 * Applies a date filter to the QueryBuilder.
 * @param qb - The QueryBuilder instance.
 * @param field - The field to filter on (e.g., "createdAt" or "user.createdAt").
 * @param from - The start date for the filter.
 * @param to - The end date for the filter (optional).
 */
const applyDateFilter = (qb, field, from, to) => {
    let alias, column;
    console.log("field", field, from, to);
    if (field.includes(".")) {
        const parts = field.split(".");
        column = parts.pop();
        alias = parts.join("_");
    }
    else {
        alias = "main";
        column = field;
    }
    if (to) {
        qb.andWhere(`${alias}.${column} BETWEEN :from AND :to`, {
            from,
            to,
        });
    }
    else {
        const startOfDay = new Date(from);
        startOfDay.setUTCHours(0, 0, 0, 0);
        const endOfDay = new Date(from);
        endOfDay.setUTCHours(23, 59, 59, 999);
        qb.andWhere(`${alias}.${column} BETWEEN :startOfDay AND :endOfDay`, {
            startOfDay,
            endOfDay,
        });
    }
};
exports.applyDateFilter = applyDateFilter;
